import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getCurrentSeason } from '@/lib/season';

interface SeasonContextValue {
  /** The active season for the current league (from league settings or calculated) */
  season: string;
  /** Whether the user is viewing a different season than the league's active season */
  isViewingHistory: boolean;
  /** Set a specific season to view (for browsing past seasons) */
  setSeason: (season: string) => void;
  /** Reset to the league's active season */
  resetToActive: () => void;
  /** The league's configured active season */
  leagueActiveSeason: string;
  /** List of available seasons (generated from the active season's format) */
  availableSeasons: string[];
}

const SeasonContext = createContext<SeasonContextValue | undefined>(undefined);

export const SeasonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentMembership } = useAuth();

  // The league's configured season, falling back to calculated current season
  const leagueActiveSeason = currentMembership?.league?.season || getCurrentSeason();

  const [selectedSeason, setSelectedSeason] = useState<string>(leagueActiveSeason);

  // When the league changes, reset to its active season
  useEffect(() => {
    setSelectedSeason(leagueActiveSeason);
  }, [leagueActiveSeason]);

  const isViewingHistory = selectedSeason !== leagueActiveSeason;

  const resetToActive = useCallback(() => {
    setSelectedSeason(leagueActiveSeason);
  }, [leagueActiveSeason]);

  // Generate available seasons list based on the league's season format
  const availableSeasons = useMemo(() => {
    const seasons: string[] = [];
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    // Detect format from the league's active season string
    const isCalendarYear = /^\d{4}$/.test(leagueActiveSeason);

    if (isCalendarYear) {
      // Calendar-year leagues (MLS): "2026", "2025", etc.
      const latestYear = currentMonth <= 1 ? currentYear - 1 : currentYear;
      for (let year = latestYear; year >= 2025; year--) {
        seasons.push(String(year));
      }
    } else {
      // Cross-year leagues (EPL): "2025-26", "2024-25", etc.
      const latestStartYear = currentMonth <= 4 ? currentYear - 1 : currentYear;
      for (let year = latestStartYear; year >= 2025; year--) {
        seasons.push(`${year}-${String(year + 1).slice(2)}`);
      }
    }

    return seasons;
  }, [leagueActiveSeason]);

  const value: SeasonContextValue = {
    season: selectedSeason,
    isViewingHistory,
    setSeason: setSelectedSeason,
    resetToActive,
    leagueActiveSeason,
    availableSeasons,
  };

  return (
    <SeasonContext.Provider value={value}>
      {children}
    </SeasonContext.Provider>
  );
};

export function useSeason(): SeasonContextValue {
  const ctx = useContext(SeasonContext);
  if (!ctx) {
    throw new Error('useSeason must be used within a SeasonProvider');
  }
  return ctx;
}
